import { TemplateType } from '@components/Common/CommonEnums';
import { usePageSetupContext } from '@context/PageSetupContext';
import { useTOCContext } from '@context/TOCContext';
import TemplateDropdowns from '@deckManagement/components/TOC/CellRenderers/TemplateDropdowns';
import { MenuItem } from '@mui/material';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import InputLabel from '@mui/material/InputLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useEffect, useMemo } from 'react';
import { GranularTemplateData } from 'types/Dialog.types';

/** Max block headers allowed to be assigned */
const MAX_BLOCK_HEADERS = 4;

// Current unsupported template selections (Total Summary, Segment Summary)
// TODO: Remove when support for page creations is implemented
const UNSUPPORTED_TEMPLATES = ['Template1', 'Template2'];

const TableConfiguration = () => {
  const {
    templateType,
    setTemplateType,
    numberOfBlockHeaders,
    setNumberOfBlockHeaders,
    selectedTemplate,
    setSelectedTemplate,
    selectedMapping,
    setSelectedMapping,
    blockHeaders,
    setBlockHeaders,
    grainTemplates,
    setGrainTemplates,
    grainMappings,
    setGrainMappings,
    grainBlockHeaders,
    setGrainBlockHeaders,
    availableGrains,
    pageSections,
    activePageSection,
    setActivePageSection,
  } = usePageSetupContext();

  const { deckColumnsForDeck } = useTOCContext();

  /**
   * Memoized data for column template dropdowns
   * @returns {Object} - Object containing template options, block header options, column mapping group options, and grain template lookup map
   */
  const { templateOptions, blockHeaderOptions, columnMappingGroupOptions, grainTemplateLookupMap } = useMemo(() => {
    if (!deckColumnsForDeck || !Array.isArray(deckColumnsForDeck))
      return {
        templateOptions: [],
        blockHeaderOptions: new Map() as Map<string, Set<string>>,
        columnMappingGroupOptions: {} as Record<string, string[]>,
        grainTemplateLookupMap: new Map(),
      };

    try {
      const templates = new Set<string>();
      const colBlockHeaders: Map<string, Set<string>> = new Map();
      const templateToColumnMappingGroups: Record<string, Array<string>> = {};
      const grainTemplateLookup: Map<string, Array<string>> = new Map();

      deckColumnsForDeck.forEach((column) => {
        if (column?.column_template && !UNSUPPORTED_TEMPLATES.includes(column.column_template)) {
          // Add the template to the unique tracking set
          templates.add(column.column_template);
          // Add the blockHeader associations as exists
          if (column?.block_header) {
            const templateBlockHeaders = colBlockHeaders.get(column.column_template) || new Set();
            templateBlockHeaders.add(column.block_header);
            colBlockHeaders.set(column.column_template, templateBlockHeaders);
          }
          // Add the template to mapping group lookup
          if (column?.column_mapping_group) {
            if (!templateToColumnMappingGroups[column.column_template]) {
              templateToColumnMappingGroups[column.column_template] = [];
            }
            if (!templateToColumnMappingGroups[column.column_template].includes(column.column_mapping_group)) {
              templateToColumnMappingGroups[column.column_template].push(column.column_mapping_group);
            }

            // Include a grain to template lookup entry
            if (column?.time_grain) {
              const grainLookups = grainTemplateLookup.get(column.time_grain) || [];
              if (!grainLookups.includes(column.column_template)) grainLookups.push(column.column_template);
              grainTemplateLookup.set(column.time_grain, grainLookups);
            }
          }
        }
      });

      /**
       * Sort template options and return all items
       */

      // Sort the mapping groups for each template
      Object.keys(templateToColumnMappingGroups).forEach((template) => {
        templateToColumnMappingGroups[template].sort();
      });

      return {
        templateOptions: Array.from(templates).sort(),
        blockHeaderOptions: colBlockHeaders,
        columnMappingGroupOptions: templateToColumnMappingGroups,
        grainTemplateLookupMap: grainTemplateLookup,
      };
    } catch (error) {
      console.error('Error processing deck column template config options:', error);
      return {
        templateOptions: [],
        blockHeaderOptions: new Map() as Map<string, Set<string>>,
        columnMappingGroupOptions: {} as Record<string, string[]>,
        grainTemplateLookupMap: new Map(),
      };
    }
  }, [deckColumnsForDeck]);

  /**
   * Clears template values when switching between single and granular modes
   */
  const clearPreviousTemplateTypeValues = (newType: TemplateType) => {
    if (newType === TemplateType.SINGLE) {
      // Clear granular values when switching to single
      setGrainTemplates({});
      setGrainMappings({});
      setGrainBlockHeaders({});
    } else if (newType === TemplateType.GRANULAR) {
      // Clear single values when switching to granular (when no existing configuration props exist)
      // TODO: For Edit mode, preserve existing values if existingTemplate/existingMapping/existingBlockHeaders are present
      setSelectedTemplate('');
      setSelectedMapping('');
      setBlockHeaders({});
    }

    // Reset number of block headers to 1 when switching
    setNumberOfBlockHeaders(1);
  };

  /**
   * Sets template and mapping values for granular configuration
   */
  const setTemplateAndMappingAtGrainLevel = (grain: string, field: 'template' | 'mapping', value: string) => {
    if (field === 'template') {
      setGrainTemplates((prev: Record<string, string>) => ({ ...prev, [grain]: value }));
      // Clear mapping when template changes
      setGrainMappings((prev: Record<string, string>) => ({ ...prev, [grain]: '' }));
    } else {
      setGrainMappings((prev: Record<string, string>) => ({ ...prev, [grain]: value }));
    }
  };

  /**
   * Sets block header values for granular configuration (per grain)
   */
  const setGrainBlockHeader = (grain: string, headerIndex: number, value: string) => {
    setGrainBlockHeaders((prev: Record<string, Record<string, string>>) => ({
      ...prev,
      [grain]: {
        ...prev[grain],
        [`blockHeader${headerIndex}`]: value,
      },
    }));
  };

  const mappingOptions = selectedTemplate ? columnMappingGroupOptions[selectedTemplate] || [] : [];

  // Create dynamic granular data structure based on available grains
  const granularData = useMemo(() => {
    return availableGrains.reduce(
      (acc, grain) => {
        acc[grain] = {
          template: grainTemplates[grain] || '',
          mapping: grainMappings[grain] || '',
          templateOptions,
          mappingOptions: grainTemplates[grain] ? columnMappingGroupOptions[grainTemplates[grain]] || [] : [],
        };
        return acc;
      },
      {} as Record<string, GranularTemplateData>,
    );
  }, [availableGrains, grainTemplates, grainMappings, templateOptions, columnMappingGroupOptions]);

  /**
   * Updates block headers state as the number of block headers changes
   */
  useEffect(() => {
    // Dynamic block headers
    setBlockHeaders((prev: Record<string, string>) =>
      Object.fromEntries(
        Array.from({ length: numberOfBlockHeaders }, (_, i) => [
          `blockHeader${i + 1}`,
          prev[`blockHeader${i + 1}`] || '', // Use prev value if exists, or empty string
        ]),
      ),
    );
  }, [numberOfBlockHeaders, setBlockHeaders]);

  // Template selection is disabled for multi-section pages (shared across all sections)
  const isMultiSection = pageSections.length > 1;

  return (
    <Box className='table-configuration-step'>
      <Typography variant='h6'>Column Template Settings</Typography>

      {isMultiSection && (
        <Tabs
          value={activePageSection}
          onChange={(_, newValue: string) => setActivePageSection(newValue)}
          variant='scrollable'
          scrollButtons='auto'
        >
          {pageSections.map((section) => (
            <Tab key={section} label={`Section ${section}`} value={section} />
          ))}
        </Tabs>
      )}

      <FormControl component='fieldset' margin='normal'>
        <RadioGroup
          value={templateType}
          onChange={(event) => {
            const newType = event.target.value as TemplateType;
            setTemplateType(newType);
            clearPreviousTemplateTypeValues(newType);
          }}
        >
          <FormControlLabel value={TemplateType.SINGLE} control={<Radio />} label='Single Template' />
          <FormControlLabel
            value={TemplateType.GRANULAR}
            control={<Radio />}
            label='Reporting Period Specific Templates'
            disabled={isMultiSection} // Granular + multi-section is not a supported combination
          />
        </RadioGroup>
      </FormControl>

      <FormControl className='num-block-headers-selector' fullWidth margin='normal' variant='outlined' size='small'>
        <InputLabel>Number of Block Headers</InputLabel>
        <Select
          value={numberOfBlockHeaders}
          onChange={(event) => setNumberOfBlockHeaders(Number(event.target.value))}
          label='Number of Block Headers'
        >
          {Array.from({ length: MAX_BLOCK_HEADERS }, (_, idx) => {
            const blockHeaderNo = idx + 1;
            return (
              <MenuItem key={blockHeaderNo} value={blockHeaderNo}>
                {blockHeaderNo}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <TemplateDropdowns
        templateType={templateType}
        selectedTemplate={selectedTemplate}
        selectedMapping={selectedMapping}
        templateOptions={templateOptions}
        mappingOptions={mappingOptions}
        granularData={granularData}
        availableGrains={availableGrains}
        templateDisabled={isMultiSection}
        onTemplateChange={(value) => {
          setSelectedTemplate(value);
          setSelectedMapping('');
        }}
        onMappingChange={setSelectedMapping}
        onGranularChange={setTemplateAndMappingAtGrainLevel}
        numberOfBlockHeaders={numberOfBlockHeaders}
        blockHeaders={templateType === TemplateType.SINGLE ? blockHeaders : undefined}
        onBlockHeaderChange={
          templateType === TemplateType.SINGLE
            ? Object.fromEntries(
                Array.from({ length: numberOfBlockHeaders }, (_, i) => [
                  `setBlockHeader${i + 1}`,
                  (value: string) =>
                    setBlockHeaders((prev: Record<string, string>) => ({ ...prev, [`blockHeader${i + 1}`]: value })),
                ]),
              )
            : undefined
        }
        grainBlockHeaders={grainBlockHeaders}
        onGrainBlockHeaderChange={setGrainBlockHeader}
        blockHeaderOptions={blockHeaderOptions}
        grainTemplateLookupMap={grainTemplateLookupMap}
      />
    </Box>
  );
};

export default TableConfiguration;
